import { Button } from '../../../components/ui/button';
import { GiftsBlock, RsvpBlock, renderGalleryByPosition } from '../shared/TemplateBlocks';

const getLocationSummary = (site, locationCards) => {
  if (site.same_location && locationCards?.[0]) {
    return {
      ceremony: locationCards[0],
      reception: {
        title: 'Recepção',
        location: 'Mesmo local',
        address: locationCards[0].address || locationCards[0].location,
        time: site.reception_time || site.ceremony_time
      }
    };
  }

  return {
    ceremony: locationCards?.[0] || {
      title: 'Cerimônia',
      location: site.ceremony_location,
      address: site.ceremony_address,
      time: site.ceremony_time
    },
    reception: locationCards?.[1] || {
      title: 'Recepção',
      location: site.reception_location,
      address: site.reception_address,
      time: site.reception_time
    }
  };
};

export default function JardimSecretoTemplate(props) {
  const {
    site,
    theme,
    weddingDateLabel,
    daysUntil,
    locationCards,
    availableGifts,
    galleryPosition,
    isTemplatePreview,
    onTemplatePreviewRedirect,
    onOpenRsvp,
    onGiftClick,
    onOpenGallery,
    showCountdown,
    showGiftSection,
    showRsvpCount,
    confirmedGuests
  } = props;

  const primary = theme.primaryColor || '#1C2E1A';
  const secondary = theme.secondaryColor || '#8FAB7E';
  const soft = theme.bannerSectionBackground || '#F5F0E8';
  const page = theme.storySectionBackground || '#FBF7EF';
  const titleColor = theme.heroTitleColor || primary;
  const bodyColor = theme.bannerTextColor || theme.bodyTextColor || primary;
  const { ceremony, reception } = getLocationSummary(site, locationCards);

  const details = [
    {
      label: 'Cerimônia',
      value: ceremony?.location || ceremony?.title || 'Local da cerimônia',
      sub: [ceremony?.time, ceremony?.address].filter(Boolean).join(' · ')
    },
    {
      label: 'Recepção',
      value: reception?.location || reception?.title || 'Local da recepção',
      sub: [reception?.time, reception?.address].filter(Boolean).join(' · ')
    },
    {
      label: showRsvpCount ? 'Confirmados' : 'Contagem',
      value: showRsvpCount ? String(confirmedGuests || 0) + ' pessoa(s)' : String(daysUntil) + ' dias',
      sub: showRsvpCount ? 'Já confirmaram presença' : 'Até o grande dia'
    }
  ];

  const galleryNode = site.gallery_images?.length ? (
    <section style={{ background: theme.gallerySectionBackground || page }}>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {site.gallery_images.slice(0, 8).map((img, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onOpenGallery?.(idx)}
            className="group h-44 overflow-hidden md:h-72"
            aria-label={'Abrir foto ' + (idx + 1)}
          >
            <img
              src={img}
              alt={'Galeria ' + (idx + 1)}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>
    </section>
  ) : null;

  const openRsvp = () => (isTemplatePreview ? onTemplatePreviewRedirect?.() : onOpenRsvp?.());

  return (
    <>
      <section className="grid min-h-screen md:grid-cols-[0.9fr_1.1fr]" style={{ background: page }}>
        <div className="flex flex-col justify-center px-8 py-20 md:px-16" style={{ background: theme.bannerSectionBackground || soft }}>
          <p className="mb-6 text-[0.62rem] uppercase tracking-[0.42em]" style={{ color: secondary, fontFamily: theme.bannerTextFont }}>Jardim Secreto</p>
          <h1 className="text-5xl italic leading-none md:text-7xl" style={{ color: titleColor, fontFamily: theme.bannerTitleFont || theme.headingFont }}>{site.couple_names}</h1>
          <div className="my-8 h-px w-20" style={{ background: secondary }} />
          <p className="max-w-md text-sm leading-7" style={{ color: bodyColor, fontFamily: theme.bannerTextFont }}>{weddingDateLabel}</p>
          {showCountdown ? <p className="mt-5 text-xs uppercase tracking-[0.28em]" style={{ color: secondary }}>{daysUntil} dias para celebrar</p> : null}
          <Button
              onClick={openRsvp}
              className="mt-10 rounded-none px-10 uppercase tracking-[0.24em]"
              style={{
                background: theme.confirmButtonBackground || primary,
                color: theme.confirmButtonText || soft,
                borderColor: theme.confirmButtonBorder || primary,
                borderWidth: 1,
                borderStyle: 'solid'
              }}
            >
              Confirmar Presença
            </Button>
        </div>
        <div className="relative min-h-[420px] overflow-hidden" style={{ background: 'linear-gradient(135deg, ' + primary + ', ' + secondary + ')' }}>
          {theme.heroImage ? <img src={theme.heroImage} alt={site.couple_names} className="h-full min-h-screen w-full object-cover" /> : null}
          <div className="absolute inset-8 border" style={{ borderColor: soft + 'AA' }} />
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'after_hero', galleryNode)}
      <section className="px-6 py-20" style={{ background: page }}>
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-5 text-[0.6rem] uppercase tracking-[0.45em]" style={{ color: secondary, fontFamily: theme.storyBodyFont }}>Nossa história</p>
          <h2 className="mb-8 text-4xl italic md:text-5xl" style={{ color: theme.storyTitleColor || primary, fontFamily: theme.storyTitleFont || theme.headingFont }}>Como tudo começou</h2>
          <p className="whitespace-pre-line text-base leading-9" style={{ color: theme.storyBodyColor || bodyColor, fontFamily: theme.storyBodyFont }}>{site.story_text}</p>
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'after_story', galleryNode)}
      <section className="px-6 py-20 " style={{ background: theme.locationSectionBackground || primary }}>
        <h2
          className="mb-12 text-center text-3xl italic md:text-4xl"
          style={{ color: theme.locationTitleColor || soft, fontFamily: theme.locationTitleFont || theme.headingFont }}
        >
          O grande dia
        </h2>
        <div className="mx-auto grid max-w-5xl gap-px md:grid-cols-3" style={{ background: 'rgba(255,255,255,0.12)' }}>
          {details.map((item) => (
            <div key={item.label} className="p-8 text-center" style={{ background: theme.locationCardBackground || 'rgba(255,255,255,0.04)' }}>
              <p className="mb-4 text-[0.58rem] uppercase tracking-[0.4em]" style={{ color: theme.locationIconColor || secondary, fontFamily: theme.locationTextFont }}>{item.label}</p>
              <p className="mb-3 text-xl italic" style={{ color: theme.locationTitleColor || soft, fontFamily: theme.locationTitleFont || theme.headingFont }}>{item.value}</p>
              <p className="text-xs leading-6" style={{ color: theme.locationTextColor || 'rgba(255,255,255,0.62)', fontFamily: theme.locationTextFont }}>{item.sub}</p>
            </div>
          ))}
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'before_gifts', galleryNode)}

      <GiftsBlock
        gifts={availableGifts}
        headingFont={theme.headingFont}
        theme={theme}
        titleColor={primary}
        textColor={theme.bodyTextColor}
        secondaryColor={secondary}
        onGiftClick={onGiftClick}
        compact
        showGiftSection={showGiftSection}
      />

      {renderGalleryByPosition(galleryPosition, 'end', galleryNode)}

      <RsvpBlock
        onOpenRsvp={onOpenRsvp}
        isTemplatePreview={isTemplatePreview}
        onTemplatePreviewRedirect={onTemplatePreviewRedirect}
        primaryColor={primary}
        secondaryColor={secondary}
        sectionBackground={theme.rsvpSectionBackground || soft}
        buttonBackground={theme.confirmButtonBackground || primary}
        buttonText={theme.confirmButtonText || soft}
        buttonBorder={theme.confirmButtonBorder || primary}
        iconColor={theme.rsvpIconColor || secondary}
        titleColor={theme.rsvpTextColor || primary}
        bodyColor={theme.rsvpTextColor || bodyColor}
        countColor={theme.rsvpTextColor || bodyColor}
        headingFont={theme.rsvpTitleFont || theme.headingFont}
        bodyFont={theme.rsvpTextFont || theme.bannerTextFont}
        showRsvpCount={showRsvpCount}
        confirmedGuests={confirmedGuests}
      />

      <footer className="px-6 py-12 text-center" style={{ background: primary }}>
        <div className="text-3xl italic" style={{ color: soft, fontFamily: theme.headingFont }}>
          {site.couple_names}
        </div>
        <div className="mt-2 text-[0.65rem] uppercase tracking-[0.35em]" style={{ color: 'rgba(255,255,255,0.42)', fontFamily: theme.bannerTextFont }}>
          Virei Noiva
        </div>
      </footer>
    </>
  );
}

